import { useCallback, useSyncExternalStore } from "react"

export type LocalState = {
  settings: { music: boolean; sfx: boolean; particles: string; reducedMotion: boolean; highContrast: boolean; textSize: string; discordPresence: boolean }
}

const KEY = "cb_settings_v2"
const LEGACY_KEY = "cb_settings_v1"

const DEFAULTS: LocalState = {
  settings: { music: true, sfx: true, particles: "High", reducedMotion: false, highContrast: false, textSize: "Normal", discordPresence: true },
}

function load(): LocalState {
  try {
    const raw = localStorage.getItem(KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<LocalState>
      return { settings: { ...DEFAULTS.settings, ...(parsed.settings ?? {}) } }
    }
    const legacy = localStorage.getItem(LEGACY_KEY)
    if (legacy) {
      const parsed = JSON.parse(legacy) as Partial<LocalState>
      const merged = { ...DEFAULTS.settings, ...(parsed.settings ?? {}) }
      merged.music = true
      merged.sfx = true
      const migrated: LocalState = { settings: merged }
      try {
        localStorage.setItem(KEY, JSON.stringify(migrated))
      } catch {
        return migrated
      }
      return migrated
    }
    return { settings: { ...DEFAULTS.settings } }
  } catch {
    return { settings: { ...DEFAULTS.settings } }
  }
}

let cache: LocalState | null = null
const listeners = new Set<() => void>()
function get(): LocalState {
  if (!cache) cache = load()
  return cache
}
function save(s: LocalState) {
  cache = s
  try {
    localStorage.setItem(KEY, JSON.stringify(s))
  } catch {
    return
  }
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  const onStorage = (e: StorageEvent) => {
    if (e.key !== null && e.key !== KEY) return
    cache = load()
    cb()
  }
  const onCustom = () => {
    cb()
  }
  window.addEventListener("storage", onStorage)
  window.addEventListener("cb_settings_changed", onCustom)
  return () => {
    listeners.delete(cb)
    window.removeEventListener("storage", onStorage)
    window.removeEventListener("cb_settings_changed", onCustom)
  }
}

function getSnapshot(): LocalState {
  return get()
}

const SERVER_SNAPSHOT: LocalState = {
  settings: { music: true, sfx: true, particles: "High", reducedMotion: false, highContrast: false, textSize: "Normal", discordPresence: true },
}

function getServerSnapshot(): LocalState {
  return SERVER_SNAPSHOT
}

function notify(): void {
  for (const l of Array.from(listeners)) {
    try {
      l()
    } catch {
      continue
    }
  }
}

export function useSnapshot(): { state: LocalState; refresh: () => void } {
  const state = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
  const refresh = useCallback(() => {
    cache = load()
    notify()
    try {
      window.dispatchEvent(new Event("cb_settings_changed"))
    } catch {
      return
    }
  }, [])
  return { state, refresh }
}

export function mutate(fn: (s: LocalState) => LocalState) {
  save(fn(get()))
  notify()
  try {
    window.dispatchEvent(new Event("cb_settings_changed"))
  } catch {
    return
  }
}


